<app-uni-page
  [parent]="this"
  *ngIf="isPageReady"
  pageTitle="Administration: Order Analysis"
  [bodyTemplate]="bodyTemplate"
  [footerTemplate]="footerTemplate"
>
</app-uni-page>

<ng-template #bodyTemplate>
  <div class="order-analysis__body">
    <!-- Page title -->
    <div class="uni-page-title">
      <i class="pi pi-search-plus"></i>
      <span>Administration: Order Analysis</span>
    </div>

    <!-- Toolbar: filter + lookup shortcut -->
    <div class="qb-list-actions flex flex-wrap items-center gap-3 p-3">
      <label class="text-sm font-semibold" for="accountFilter">Account contains:</label>
      <input
        id="accountFilter"
        type="text"
        pInputText
        class="w-64"
        placeholder="Filter by account name"
        title="Account name filter"
        [(ngModel)]="accountNameContains"
        (ngModelChange)="onAccountFilterChange()"
      />
      <span class="ml-2 text-xs text-gray-500">{{ cards.length }} of {{ totalCount }} shown</span>

      <div class="flex-1"></div>

      <label class="text-sm font-semibold" for="lookupKey">Lookup:</label>
      <input
        id="lookupKey"
        type="text"
        pInputText
        class="w-80"
        placeholder="Paste a Proximity or a Uniformax Id"
        title="Lookup key"
        [(ngModel)]="lookupKey"
        (keyup)="onLookupKeyup($event)"
      />
      <button
        pButton
        type="button"
        class="p-button-raised p-button-sm"
        icon="pi pi-search"
        [disabled]="!lookupKey?.trim()"
        (click)="onLookupSubmit()"
      >
        <span>Open</span>
      </button>
    </div>

    <!-- Card grid -->
    <div class="order-analysis__scroll p-4">
      <div *ngIf="isLoadingList && cards.length === 0" class="flex h-full items-center justify-center">
        <p-progressSpinner ariaLabel="loading"></p-progressSpinner>
      </div>

      <div *ngIf="!isLoadingList && cards.length === 0" class="mt-8 text-center text-gray-500">No job orders match the current filter.</div>

      <div class="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
        <div
          *ngFor="let c of cards"
          class="qb-detail-card cursor-pointer p-3"
          [class.qb-primary-tint]="analyzingId === c.joHeaderId"
          (click)="analyzeCard(c)"
        >
          <!-- Header row: order # + status tag + issue dot -->
          <div class="mb-2 flex items-center justify-between border-b pb-2">
            <div class="flex items-center gap-2">
              <i *ngIf="c.hasFailedReceipt" class="pi pi-circle-fill text-red-500" title="Has at least one failed ProxReceipt"></i>
              <span class="font-mono text-sm font-semibold">Order #{{ c.joHeaderId }}</span>
              <span
                *ngIf="c.proximityJoHeaderId"
                class="font-mono text-xs text-gray-500"
                [title]="'Proximity ID: ' + c.proximityJoHeaderId"
              >
                {{ shortGuid(c.proximityJoHeaderId) }}
              </span>
            </div>
            <p-tag severity="secondary" [value]="c.joStatusName || c.joStatusId"></p-tag>
          </div>

          <!-- Body fields -->
          <div class="space-y-1 text-sm">
            <div><span class="text-gray-500">Account:</span> {{ c.accountName }} (#{{ c.accountId }})</div>
            <div><span class="text-gray-500">Created:</span> {{ c.crtnTs | date: 'medium' }}</div>
            <div><span class="text-gray-500">Expected Ship:</span> {{ c.origExpectedShipDate | date: 'mediumDate' }}</div>
            <div *ngIf="c.customerPurchaseOrderNumber"><span class="text-gray-500">PO:</span> {{ c.customerPurchaseOrderNumber }}</div>
            <div class="flex flex-wrap gap-2 pt-2">
              <p-tag *ngIf="c.isAccountOwnedOrder" severity="info" value="Account-owned"></p-tag>
              <p-tag *ngIf="c.mustShipComplete" severity="warn" value="Must ship complete"></p-tag>
            </div>
          </div>

          <!-- Footer: analyze button -->
          <div class="mt-3 border-t pt-2">
            <button
              pButton
              type="button"
              class="p-button-raised p-button-sm w-full"
              icon="pi pi-search"
              [loading]="isAnalyzing && analyzingId === c.joHeaderId"
              (click)="$event.stopPropagation(); analyzeCard(c)"
            >
              <span>Analyze</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Load more -->
      <div *ngIf="cards.length < totalCount" class="mt-4 flex justify-center">
        <button pButton type="button" class="p-button-text p-button-sm" [loading]="isLoadingList" (click)="loadMore()">
          <span>Load more ({{ totalCount - cards.length }} remaining)</span>
        </button>
      </div>
    </div>
  </div>
</ng-template>

<!-- ============== ANALYSIS DRAWER ============== -->
<p-drawer [(visible)]="drawerVisible" position="right" [style]="{ width: '900px' }" (onHide)="onDrawerHide()">
  <ng-template pTemplate="header">
    <div class="flex items-center gap-2">
      <span class="font-semibold">Order Analysis</span>
      <span *ngIf="result?.joHeader" class="font-mono text-sm text-gray-500"> Order #{{ result?.joHeader?.id }} </span>
      <span
        *ngIf="result?.joHeader?.proximityJoHeaderId"
        class="font-mono text-xs text-gray-500"
        [title]="'Proximity ID: ' + result?.joHeader?.proximityJoHeaderId"
      >
        · {{ shortGuid(result?.joHeader?.proximityJoHeaderId ?? null) }}
      </span>
    </div>
  </ng-template>

  <!-- Loading -->
  <div *ngIf="isAnalyzing" class="flex h-full items-center justify-center">
    <p-progressSpinner ariaLabel="analyzing"></p-progressSpinner>
  </div>

  <!-- Error -->
  <div *ngIf="analysisError && !isAnalyzing" class="p-3 text-red-700">
    {{ analysisError }}
  </div>

  <!-- Result -->
  <div *ngIf="result && !isAnalyzing" class="flex flex-col gap-4">
    <!-- Resolution echo + tiny header strip -->
    <div class="text-sm text-gray-600">
      Input <b>{{ result.inputKey }}</b> resolved as <b>{{ result.resolvedAs }}</b
      >.
    </div>

    <!-- JO HEADER SUMMARY (compact, one card) -->
    <div *ngIf="result.joHeader" class="qb-detail-card qb-primary-tint p-3">
      <div class="grid grid-cols-2 gap-2 text-sm">
        <div>
          <span class="text-gray-500">Account:</span>
          {{ result.account?.accountName || '—' }}
          <span *ngIf="result.account">(#{{ result.account?.id }})</span>
        </div>
        <div>
          <span class="text-gray-500">Order Status:</span>
          {{ result.joHeader?.joStatusName || result.joHeader?.joStatusId }}
        </div>
        <div><span class="text-gray-500">Employee:</span> {{ result.joHeader?.empEmail || '—' }}</div>
        <div><span class="text-gray-500">Created:</span> {{ result.joHeader?.crtnTs | date: 'medium' }}</div>
        <div><span class="text-gray-500">Expected Ship:</span> {{ result.joHeader?.origExpectedShipDate | date: 'mediumDate' }}</div>
        <div><span class="text-gray-500">Detail count:</span> {{ result.joDetailCount }}</div>
        <div><span class="text-gray-500">Invoices:</span> {{ result.invoices.length }}</div>
        <div><span class="text-gray-500">Shipments:</span> {{ result.shipments.length }}</div>
      </div>
      <div *ngIf="result.joHeader?.mustShipComplete" class="flex flex-wrap gap-2 pt-2">
        <p-tag severity="warn" value="Must ship complete"></p-tag>
      </div>
    </div>

    <!-- RED FLAGS -->
    <div class="qb-detail-card p-3">
      <div class="mb-2 border-b pb-2 text-sm font-semibold uppercase tracking-wide">
        Red Flags
        <span *ngIf="result.redFlags?.length" class="font-normal lowercase"> ({{ result.redFlags.length }}) </span>
      </div>
      <div *ngIf="!result.redFlags?.length" class="text-sm text-gray-600">No red flags detected.</div>
      <div *ngFor="let flag of result.redFlags" class="mb-3 last:mb-0">
        <div class="flex items-center gap-2">
          <p-tag [severity]="redFlagSeverityTag(flag.severity)" [value]="flag.severity"></p-tag>
          <span class="font-mono text-xs text-gray-500">{{ flag.id }}</span>
          <span class="text-sm font-semibold">{{ flag.title }}</span>
        </div>
        <div class="ml-1 mt-1 text-sm text-gray-700">{{ flag.detail }}</div>
      </div>
    </div>

    <!-- BEHAVIOR CHECKS -->
    <div class="qb-detail-card p-3">
      <div class="mb-2 border-b pb-2 text-sm font-semibold uppercase tracking-wide">
        Behavior Checks
        <span class="font-normal lowercase"> ({{ result.behaviorChecks?.length || 0 }}) </span>
      </div>

      <div *ngIf="!result.behaviorChecks?.length" class="text-sm text-gray-600">No behavior checks available.</div>

      <div *ngFor="let group of groupedChecks()" class="mb-4 last:mb-0">
        <div class="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
          {{ group.category }}
        </div>
        <table class="qb-info-table w-full table-fixed">
          <tr>
            <th class="label w-[14%]">Check</th>
            <th class="label w-[12%]">Status</th>
            <th class="label w-[28%]">Expected</th>
            <th class="label w-[28%]">Actual</th>
            <th class="label">Detail</th>
          </tr>
          <tr *ngFor="let chk of group.checks">
            <td class="value">
              <span class="mr-1 font-mono text-xs text-gray-500">{{ chk.id }}</span>
              {{ chk.title }}
            </td>
            <td class="value">
              <p-tag [severity]="checkStatusTag(chk.status)" [value]="chk.status" [icon]="checkStatusIcon(chk.status)"></p-tag>
            </td>
            <td class="value">{{ chk.expected }}</td>
            <td class="value" [class.text-red-700]="chk.status === 'Fail'">{{ chk.actual }}</td>
            <td class="value text-xs text-gray-600">{{ chk.detail || '' }}</td>
          </tr>
        </table>
      </div>
    </div>
  </div>
</p-drawer>

<ng-template #footerTemplate>
  <div class="left-buttons">
    <button pButton type="button" class="p-button-raised" (click)="goBack()">
      <span>Go Back</span>
    </button>
  </div>
</ng-template>
